import { ApiResponseError, NetworkError, type ApiErrorBody } from './errors'

export interface UploadProgress {
  loaded: number
  total: number | null
}

export interface ApiRequestOptions extends RequestInit {
  onUploadProgress?: (progress: UploadProgress) => void
}

export class RestApiClient {
  constructor(private readonly baseUrl: string) {}

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    options.signal?.throwIfAborted()
    // XHR cannot honor credentials: 'omit' for a same-origin URL.
    // Reject before sending rather than silently including browser cookies.
    if (
      options.onUploadProgress &&
      options.credentials === 'omit' &&
      new URL(`${this.baseUrl}${path}`, window.location.href).origin ===
        window.location.origin
    ) {
      throw new Error(
        'Upload progress requires a backend on a separate origin to avoid sending cookies.',
      )
    }
    let response: Response
    try {
      const { onUploadProgress, ...requestOptions } = options
      response = onUploadProgress
        ? await this.requestWithUploadProgress(
            path,
            requestOptions,
            onUploadProgress,
          )
        : await fetch(`${this.baseUrl}${path}`, requestOptions)
    } catch (error) {
      if (options.signal?.aborted) throw error
      throw new NetworkError(error)
    }

    if (!response.ok) {
      const body: ApiErrorBody = await response.json().catch(() => ({}))
      throw new ApiResponseError(response.status, body)
    }
    if (response.status === 204) return undefined as T
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  }

  /** Only requests that opt into upload progress use XHR; response handling is shared. */
  private requestWithUploadProgress(
    path: string,
    options: RequestInit,
    onUploadProgress: NonNullable<ApiRequestOptions['onUploadProgress']>,
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const signal = options.signal
      const abort = () => {
        xhr.abort()
        reject(
          signal?.reason ??
            new DOMException('The request was aborted', 'AbortError'),
        )
      }
      const cleanup = () => signal?.removeEventListener('abort', abort)

      // Register upload listeners before open() for browser compatibility.
      xhr.upload.onprogress = (event) =>
        onUploadProgress({
          loaded: event.loaded,
          total: event.lengthComputable ? event.total : null,
        })
      xhr.upload.onload = (event) =>
        onUploadProgress({
          loaded: event.loaded,
          total: event.loaded,
        })
      xhr.onload = () => {
        try {
          resolve(
            new Response(xhr.responseText || null, {
              status: xhr.status,
              statusText: xhr.statusText,
            }),
          )
        } catch (error) {
          reject(error)
        }
      }
      xhr.onerror = () => reject(new TypeError('Failed to upload'))
      xhr.ontimeout = () => reject(new TypeError('Upload timed out'))
      xhr.onabort = () =>
        reject(
          signal?.reason ??
            new DOMException('The request was aborted', 'AbortError'),
        )
      xhr.onloadend = cleanup

      try {
        xhr.open(options.method ?? 'GET', `${this.baseUrl}${path}`)
        xhr.withCredentials = options.credentials === 'include'
        new Headers(options.headers).forEach((value, name) =>
          xhr.setRequestHeader(name, value),
        )
        signal?.addEventListener('abort', abort, { once: true })
        signal?.throwIfAborted()
        // Each attempt starts afresh, including the authenticated retry after a 401.
        onUploadProgress({ loaded: 0, total: null })
        signal?.throwIfAborted()
        xhr.send(options.body as XMLHttpRequestBodyInit | null | undefined)
      } catch (error) {
        cleanup()
        reject(error)
      }
    })
  }
}
